import type { ThunkExtras, GetState, ThunkAction, APIError, Dispatch } from '../../types/app.types';

export type Card = {
  brand: string;
  default: boolean;
  expMonth: number;
  expYear: number;
  id: string;
  last4: string;
};
export type PaymentMethodsState = Array<Card>;
export type AddCard = {
  type: 'payment/ADD_CARD';
  token: string;
};
export type AddCardSuccess = {
  type: 'payment/ADD_CARD_SUCCESS';
  card: Card;
};
export type AddCardError = {
  type: 'payment/ADD_CARD_ERROR';
} & APIError;
export type RemoveCard = {
  type: 'payment/REMOVE_CARD';
};
export type RemoveCardSuccess = {
  type: 'payment/REMOVE_CARD_SUCCESS';
  card: string;
};
export type RemoveCardError = {
  type: 'payment/REMOVE_CARD_ERROR';
} & APIError;
export type GetCards = {
  type: 'payment/GET_CARDS';
};
export type GetCardsSuccess = {
  type: 'payment/GET_CARDS_SUCCESS';
  cards: Array<Card>;
};
export type GetCardsError = {
  type: 'payment/GET_CARDS_ERROR';
} & APIError;
export type MakeCardDefault = {
  type: 'payment/MAKE_CARD_DEFAULT';
  id: string;
};
export type MakeCardDefaultSuccess = {
  type: 'payment/MAKE_CARD_DEFAULT_SUCCESS';
  card: Card;
};
export type MakeCardDefaultError = {
  type: 'payment/MAKE_CARD_DEFAULT_ERROR';
} & APIError;
export type PaymentMethodsAction =
  | { type: '@INIT' }
  | AddCard
  | AddCardSuccess
  | AddCardError
  | RemoveCard
  | RemoveCardSuccess
  | RemoveCardError
  | GetCards
  | GetCardsError
  | GetCardsSuccess
  | MakeCardDefault
  | MakeCardDefaultSuccess
  | MakeCardDefaultError;

export function getDefaultCard(cards: Array<Card>): Card | null | undefined {
  if (cards && cards.length === 1) return cards[0];
  return cards.find((card) => card.default);
}

/*eslint-disable no-throw-literal*/
export function addCard(tokenID: string): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, extras: ThunkExtras): Promise<any> {
    return extras.blinkAPIClient
      .post('/payments/add_token', {
        stripeCardToken: tokenID,
      })
      .then((res) => {
        return res.data;
      })
      .then(({ card }: { card: Card }) =>
        dispatch({
          type: 'payment/ADD_CARD_SUCCESS',
          card,
        })
      )
      .then(() => dispatch(getCards()))
      .catch((err) => {
        dispatch({
          type: 'payment/ADD_CARD_ERROR',
          ...err,
        });
        throw {
          type: 'CardError',
          ...err,
        };
      });
  };
}

/*eslint-enable no-throw-literal*/
export function makeCardDefault(cardId: string): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, extras: ThunkExtras): Promise<any> {
    return extras.blinkAPIClient
      .post(`/payments/cards/${cardId}/make_default`)
      .then((res) => res.data)
      .then((res) =>
        dispatch({
          type: 'payment/MAKE_CARD_DEFAULT_SUCCESS',
          card: { ...res },
        })
      )
      .catch((err) => {
        dispatch({
          type: 'payment/MAKE_CARD_DEFAULT_ERROR',
          ...err,
        });
        throw err;
      });
  };
}

export function getCards(): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, extras: ThunkExtras): Promise<any> {
    return extras.blinkAPIClient
      .get('/cards')
      .then((res) => res.data)
      .then((res) => {
        return dispatch({
          type: 'payment/GET_CARDS_SUCCESS',
          cards: res.results,
        });
      })
      .catch((err) => {
        return dispatch({
          type: 'payment/GET_CARDS_ERROR',
          ...err,
        });
      });
  };
}

export function removeCard(cardId: string): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, extras: ThunkExtras): Promise<any> {
    return extras.blinkAPIClient
      .delete(`/payments/cards/${cardId}`)
      .then((res) => res.data)
      .then(() =>
        dispatch({
          type: 'payment/REMOVE_CARD_SUCCESS',
          card: cardId,
        })
      )
      .then(() => dispatch(getCards()))
      .catch((err) => {
        dispatch({
          type: 'payment/REMOVE_CARD_ERROR',
          ...err,
        });
        throw err;
      });
  };
}

export const initialState: PaymentMethodsState = [];
export default function paymentMethodsReducer(
  state: PaymentMethodsState = initialState,
  action: PaymentMethodsAction
): PaymentMethodsState {
  switch (action.type) {
    case 'payment/ADD_CARD_SUCCESS': {
      return [
        action.card,
        ...state.filter((card) => card.last4 !== action.card.last4).map((card) => ({ ...card, default: false })),
      ];
    }

    case 'payment/GET_CARDS_SUCCESS': {
      return action.cards;
    }

    case 'payment/MAKE_CARD_DEFAULT_SUCCESS': {
      const actionCard = action.card;
      return [...state.map((card) => ({ ...card, default: card.id === actionCard.id }))];
    }

    case 'payment/REMOVE_CARD_SUCCESS': {
      const removedCard = state.find((card) => card.id === action.card);
      const newCards = state.filter((card) => card.id !== action.card);
      return removedCard.default ? newCards.map((card, index) => ({ ...card, default: index === 0 })) : newCards;
    }

    default:
      return state;
  }
}
